import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';

type Utils = ReturnType<typeof trpc.useUtils>;
type Groups = Awaited<ReturnType<Utils['tradingBankMatching']['groups']['fetch']>>;
type Caris = Awaited<ReturnType<Utils['tradingCari']['list']['fetch']>>;
interface Props {
  data?: Groups;
  isLoading: boolean;
  isFetching: boolean;
  error?: string;
  scope: {
    status: 'pending' | 'completed' | 'all';
    search?: string;
    currency?: 'TRY' | 'EUR' | 'USD' | 'GBP';
    bankId?: string;
  };
  page: number;
  onPageChange: (page: number) => void;
  caris?: Caris;
  canSave: boolean;
  canCreateCari: boolean;
  readOnlyPreview: boolean;
  onCreateCari: () => void;
  onIndividualView: (name: string) => void;
}
const money = (amount: number, currency: string) =>
  `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
const day = (date: Date | string) =>
  new Date(date).toLocaleDateString('tr-TR', { timeZone: 'UTC' });

export function GroupedBankMatching(props: Props) {
  const { t } = useTranslation('trading');
  const utils = trpc.useUtils();
  const [group, setGroup] = useState<Groups['items'][number] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [targetValue, setTargetValue] = useState('');
  const [note, setNote] = useState('');
  const detailRef = useRef<HTMLElement>(null);
  const entries = trpc.tradingBankMatching.groupEntries.useQuery(
    { ...props.scope, groupKey: group?.key ?? '' },
    { enabled: !!group }
  );
  const pendingIds = (entries.data?.items ?? [])
    .filter((r) => r.status === 'pending')
    .map((r) => r.id);
  const ids = [...(selectedIds ?? pendingIds)].filter((id) => pendingIds.includes(id)).sort();
  const sourceBankIds = new Set(
    (entries.data?.items ?? []).filter((r) => ids.includes(r.id)).map((r) => r.bankId)
  );
  const target = props.caris?.items.find(
    (p) =>
      `${p.source}:${p.id}` === targetValue && !(p.source === 'supplier' && sourceBankIds.has(p.id))
  );
  const selection = {
    groupKey: group?.key ?? '',
    bankMovementIds: ids,
    target: { source: target?.source ?? 'customer', id: target?.id ?? '' },
  };
  const preview = trpc.tradingBankMatching.previewGroup.useQuery(selection, {
    enabled: !!group && !!target && ids.length > 0 && !entries.isFetching,
  });
  const save = trpc.tradingBankMatching.saveGroup.useMutation({
    onSuccess: async (result) => {
      await Promise.all([utils.tradingBankMatching.invalidate(), utils.tradingCari.invalidate()]);
      showSuccessToast(
        t('bankMatching.groupSaved', {
          count: result.savedCount,
          defaultValue: `${result.savedCount} hareket eşleştirildi.`,
        })
      );
      setGroup(null);
      setSelectedIds(null);
      setTargetValue('');
    },
    onError: () => {
      void preview.refetch();
    },
  });
  useEffect(() => {
    if (group) detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [group?.key]);
  const options = (props.caris?.items ?? [])
    .filter((p) => !(p.source === 'supplier' && sourceBankIds.has(p.id)))
    .map((p) => ({
      value: `${p.source}:${p.id}`,
      label: p.companyName,
      description: `${p.cariType}${p.legacyCariCode ? ` · ${p.legacyCariCode}` : ''}`,
      keywords: [p.companyName, p.cariType, p.legacyCariCode ?? ''],
    }));
  const ready = preview.data?.readyCount ?? 0;
  const canSubmit =
    props.canSave &&
    ids.length > 0 &&
    !!target &&
    !!preview.data &&
    ready > 0 &&
    !preview.isFetching &&
    !preview.error &&
    !entries.isFetching &&
    !save.isPending;
  const rowStates = new Map(preview.data?.rows.map((r) => [r.id, r]) ?? []);
  const choose = (item: Groups['items'][number]) => {
    setGroup(item);
    setSelectedIds(null);
    setTargetValue('');
    setNote('');
    save.reset();
  };
  const submit = () => {
    if (!canSubmit || !preview.data) return;
    save.mutate({
      ...selection,
      previewKey: preview.data.previewKey,
      note: note.trim() || undefined,
    });
  };
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {t(
          'bankMatching.groupHelp',
          'Aynı Gönderici / Alıcıya ait hareketleri bir cariyle topluca eşleştirin. Muhasebe hesap numarasıyla eşleştirmek için Tek hareket görünümünü kullanın.'
        )}{' '}
        <span className="font-medium">
          {props.data?.groupCount ?? 0} {t('bankMatching.uniqueNames', 'farklı Gönderici / Alıcı')}
        </span>
      </p>
      {group && (
        <section
          ref={detailRef}
          className="bg-card scroll-mt-4 space-y-4 rounded-lg border p-4 sm:p-5"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold">
                {group.name || t('bankMatching.unnamed', 'Açıklamada isim yok')}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {ids.length} / {pendingIds.length}{' '}
                {t('bankMatching.selectedPending', 'bekleyen hareket seçildi')}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setGroup(null)}
              disabled={save.isPending}
            >
              {t('bankMatching.close', 'Detayı kapat')}
            </Button>
          </div>
          {entries.error && (
            <p role="alert" className="text-destructive text-sm">
              {entries.error.message}
            </p>
          )}
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!pendingIds.length && ids.length === pendingIds.length}
                  disabled={!pendingIds.length || entries.isFetching || save.isPending}
                  onChange={(e) => setSelectedIds(e.target.checked ? pendingIds : [])}
                />
                {t('bankMatching.selectAllPending', 'Tüm bekleyen hareketleri seç')}
              </label>
              <div className="max-h-96 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="p-3 text-left">{t('bankMatching.date', 'Tarih')}</th>
                      <th className="p-3 text-left">
                        {t('bankMatching.groupMovement', 'Hareket / Kontrol')}
                      </th>
                      <th className="p-3 text-right">{t('bankMatching.amount', 'Banka tutarı')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.isLoading && (
                      <tr>
                        <td colSpan={3} className="p-8 text-center">
                          <Loader2 className="inline h-5 w-5 animate-spin" />
                        </td>
                      </tr>
                    )}
                    {entries.data?.items.map((row) => (
                      <tr key={row.id} className="border-t">
                        <td className="whitespace-nowrap p-3 align-top">
                          <label className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              aria-label={`${t('bankMatching.selectMovement', 'Hareketi seç')}: ${row.reference}`}
                              checked={ids.includes(row.id)}
                              disabled={
                                row.status !== 'pending' || save.isPending || entries.isFetching
                              }
                              onChange={(e) =>
                                setSelectedIds(
                                  e.target.checked
                                    ? [...ids, row.id]
                                    : ids.filter((id) => id !== row.id)
                                )
                              }
                            />
                            <span>
                              {day(row.date)}
                              <span className="text-muted-foreground mt-1 block">
                                {row.reference}
                              </span>
                            </span>
                          </label>
                        </td>
                        <td className="p-3">
                          <p className="line-clamp-2" title={row.description}>
                            {row.description}
                          </p>
                          <p className="text-muted-foreground mt-1">{row.bankName}</p>
                          {row.status === 'completed' ? (
                            <p className="mt-1 text-emerald-700">
                              {row.resolution?.targetName ||
                                t('bankMatching.completed', 'Tamamlanan')}
                            </p>
                          ) : rowStates.get(row.id)?.status === 'review' ? (
                            <p className="mt-1 text-amber-800">{rowStates.get(row.id)?.reason}</p>
                          ) : rowStates.get(row.id)?.status === 'ready' ? (
                            <p className="mt-1 text-emerald-700">
                              {t('bankMatching.ready', 'Kayda hazır')}
                            </p>
                          ) : null}
                        </td>
                        <td
                          className={`whitespace-nowrap p-3 text-right align-top ${row.flow === 'incoming' ? 'text-emerald-700' : 'text-rose-700'}`}
                        >
                          {money(row.amount, row.currency)}
                          <p className="mt-1">
                            {row.flow === 'incoming'
                              ? t('bankMatching.incoming', 'Banka girişi')
                              : t('bankMatching.outgoing', 'Banka çıkışı')}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                variant="link"
                size="sm"
                onClick={() => props.onIndividualView(group.name)}
                disabled={save.isPending}
              >
                {t(
                  'bankMatching.reviewIndividually',
                  'Ayrıntılı eşleştirme için Tek hareket görünümünü aç'
                )}
              </Button>
            </div>
            <div className="min-w-0 space-y-4">
              <div className="space-y-2">
                <Label>{t('bankMatching.groupTarget', 'Bu Gönderici / Alıcı için cari')}</Label>
                <Combobox
                  ariaLabel={t('bankMatching.groupTarget', 'Bu Gönderici / Alıcı için cari')}
                  value={targetValue}
                  options={options}
                  onValueChange={(v) => {
                    setTargetValue(v);
                    save.reset();
                  }}
                  placeholder={t('bankMatching.selectCari', 'Cari seçin')}
                  searchPlaceholder={t('bankMatching.searchCari', 'Cari adı veya kodu ara')}
                  emptyText={t(
                    'bankMatching.noCari',
                    'Cari bulunamadı. Yeni Cari ile oluşturabilirsiniz.'
                  )}
                  disabled={save.isPending || !pendingIds.length}
                />
                {props.canCreateCari && (
                  <button
                    type="button"
                    className="text-primary text-xs underline"
                    onClick={props.onCreateCari}
                  >
                    {t('bankMatching.missingCari', 'Cari kartı yoksa yeni oluştur')}
                  </button>
                )}
              </div>
              {preview.isFetching && (
                <p className="text-muted-foreground flex items-center gap-2 text-xs">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('bankMatching.previewLoading', 'Cari etkisi hesaplanıyor…')}
                </p>
              )}
              {preview.error && (
                <p role="alert" className="text-destructive text-sm">
                  {preview.error.message}
                </p>
              )}
              {preview.data && (
                <div className="space-y-3 rounded-md border p-3 text-xs">
                  <p className="font-semibold">
                    {ready} {t('bankMatching.groupReady', 'hareket kaydedilecek')} ·{' '}
                    {preview.data.reviewCount}{' '}
                    {t('bankMatching.groupReview', 'hareket incelemede kalacak')}
                  </p>
                  {preview.data.balances.map((b) => (
                    <div key={b.currency} className="space-y-1 border-t pt-2">
                      <p className="font-semibold">{b.currency}</p>
                      <p>
                        {t('bankMatching.groupDebit', 'Cari borç toplamı')}:{' '}
                        {money(b.debit, b.currency)}
                      </p>
                      <p>
                        {t('bankMatching.groupCredit', 'Cari alacak toplamı')}:{' '}
                        {money(b.credit, b.currency)}
                      </p>
                      <p>
                        {t('bankMatching.balanceBefore', 'Mevcut cari bakiyesi')}:{' '}
                        {money(b.balanceBefore, b.currency)}
                      </p>
                      <p>
                        {t('bankMatching.balanceAfter', 'Kaydetme sonrası bakiye')}:{' '}
                        {money(b.balanceAfter, b.currency)}
                      </p>
                      <p className="text-muted-foreground">
                        {t('bankMatching.groupTryDebit', 'TL borç')}: {money(b.tryDebit, 'TRY')} ·{' '}
                        {t('bankMatching.groupTryCredit', 'TL alacak')}: {money(b.tryCredit, 'TRY')}
                      </p>
                    </div>
                  ))}
                  {preview.data.reviewCount > 0 && (
                    <p className="text-amber-800">
                      {t(
                        'bankMatching.groupReviewHelp',
                        'Mevcut kayıt veya kredi ayrımı gerektiren hareketler kaydedilmez; satırdaki nedeni kontrol edip Tek hareket görünümünde tamamlayabilirsiniz.'
                      )}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="bank-group-note">{t('bankMatching.note', 'Açıklama')}</Label>
                <Textarea
                  id="bank-group-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                  rows={2}
                  disabled={save.isPending}
                />
              </div>
              {save.error && (
                <p role="alert" className="text-destructive text-sm">
                  {save.error.message}
                </p>
              )}
              {props.readOnlyPreview && (
                <p className="text-muted-foreground text-xs">
                  {t(
                    'bankMatching.readOnlyPreview',
                    'Bu önizleme salt okunurdur. Eşleştirmeler canlı uygulamada kaydedilebilir.'
                  )}
                </p>
              )}
              {!props.canSave && !props.readOnlyPreview && (
                <p className="text-muted-foreground text-xs">
                  {t('bankMatching.noPermission', 'Kaydetmek için cari oluşturma yetkisi gerekir.')}
                </p>
              )}
              <Button className="w-full" disabled={!canSubmit} onClick={submit}>
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('bankMatching.saveGroup', {
                  count: ready,
                  defaultValue: `${ready} Hareketi Eşleştir`,
                })}
              </Button>
              <p className="text-muted-foreground text-xs">
                {t(
                  'bankMatching.groupPreserve',
                  'Seçilen hareketler ayrı ayrı kendi tarih, döviz, kur ve referanslarıyla kaydedilir.'
                )}
              </p>
            </div>
          </div>
        </section>
      )}
      <section className="bg-card min-w-0 rounded-lg border">
        {props.error && (
          <p role="alert" className="text-destructive p-4 text-sm">
            {props.error}
          </p>
        )}
        {props.isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="inline h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr>
                  <th className="p-4 text-left">
                    {t('bankMatching.counterparty', 'Gönderici / Alıcı')}
                  </th>
                  <th className="p-4 text-right">
                    {t('bankMatching.groupCount', 'Hareket sayısı')}
                  </th>
                  <th className="p-4 text-right">
                    {t('bankMatching.groupTotals', 'Banka giriş / çıkış toplamı')}
                  </th>
                  <th className="p-4 text-right">{t('bankMatching.action', 'İşlem')}</th>
                </tr>
              </thead>
              <tbody>
                {props.data?.items.map((item) => (
                  <tr
                    key={item.key}
                    className={`border-b last:border-0 ${group?.key === item.key ? 'bg-primary/5' : ''}`}
                  >
                    <td className="p-4">
                      <p className="font-medium">
                        {item.name || t('bankMatching.unnamed', 'Açıklamada isim yok')}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {day(item.firstDate)} – {day(item.lastDate)} · {item.bankCount}{' '}
                        {t('bankMatching.bankAccounts', 'banka hesabı')}
                      </p>
                    </td>
                    <td className="p-4 text-right align-top">
                      {item.count}
                      <p className="text-muted-foreground mt-1 text-xs">
                        {item.pendingCount} {t('bankMatching.groupPending', 'bekleyen')}
                      </p>
                    </td>
                    <td className="p-4 text-right align-top">
                      {item.totals.map((total) => (
                        <div key={total.currency} className="whitespace-nowrap text-xs">
                          <span className="text-emerald-700">
                            +{money(total.incoming, total.currency)}
                          </span>
                          <span className="text-muted-foreground mx-1">/</span>
                          <span className="text-rose-700">
                            −{money(total.outgoing, total.currency)}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td className="p-4 text-right align-top">
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`${t('bankMatching.matchGroup', 'Grubu eşleştir')}: ${item.name}`}
                        onClick={() => choose(item)}
                        disabled={save.isPending}
                      >
                        {item.pendingCount
                          ? t('bankMatching.match', 'Eşleştir')
                          : t('bankMatching.view', 'Görüntüle')}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!props.data?.items.length && (
                  <tr>
                    <td colSpan={4} className="text-muted-foreground p-12 text-center">
                      {t('bankMatching.empty', 'Bu filtrelerde hareket bulunamadı.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-muted-foreground flex items-center justify-between gap-3 border-t px-4 py-3 text-xs">
          <span>
            {props.data?.pagination.total ?? 0}{' '}
            {t('bankMatching.uniqueNames', 'farklı Gönderici / Alıcı')}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={props.page <= 1 || props.isFetching || save.isPending}
              onClick={() => props.onPageChange(props.page - 1)}
            >
              {t('bankMatching.previous', 'Önceki')}
            </Button>
            <span>
              {props.page} / {Math.max(1, props.data?.pagination.totalPages ?? 1)}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={
                props.page >= (props.data?.pagination.totalPages ?? 1) ||
                props.isFetching ||
                save.isPending
              }
              onClick={() => props.onPageChange(props.page + 1)}
            >
              {t('bankMatching.next', 'Sonraki')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
